import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getStudentSlideContent,
  getStudentSlidePrevNext,
  getStudentLessonDetail,
  type FullSlideContentOut,
  type SlidePrevNextOut
} from '../../lib/apis_publish';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2,
  ArrowLeft, Loader2, GripHorizontal, BookOpen, X, Sparkles
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// ✅ 引入独立拆分后的教案美化渲染器
// 请确保路径正确，如果是放在 src/components/common 下
import BeautifiedGuideRenderer from '../../components/common/BeautifiedGuideRenderer';

const StudentSlidePlayer: React.FC = () => {
  const { slideId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // --- 数据状态 ---
  const [slide, setSlide] = useState<FullSlideContentOut | null>(null);
  const [nav, setNav] = useState<SlidePrevNextOut | null>(null);
  const [totalSlides, setTotalSlides] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // --- UI 状态 ---
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // --- 拖拽状态 ---
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef<{ x: number; y: number } | null>(null);

  // 全屏锚点容器
  const containerRef = useRef<HTMLDivElement>(null);

  // 1. 数据获取与初始化
  useEffect(() => {
    if (!slideId || !lessonId) return;
    setLoading(true);
    setIsGuideOpen(false); // 切换页面时关闭教案

    // 切换页面时，重置控制栏位置到底部中央
    setPosition(null);
    positionRef.current = null;

    const fetchSlideData = async () => {
      try {
        const [slideRes, navRes, lessonRes] = await Promise.all([
          getStudentSlideContent(parseInt(slideId)),
          getStudentSlidePrevNext(parseInt(slideId)),
          getStudentLessonDetail(parseInt(lessonId)) 
        ]);
        
        setSlide(slideRes.data);
        setNav(navRes.data);
        if (lessonRes.data && lessonRes.data.slides) {
          setTotalSlides(lessonRes.data.slides.length);
        }
      } catch (err: any) {
        console.error('Error fetching slide data:', err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 500);
        }
      } finally {
        setTimeout(() => setLoading(false), 300);
      }
    };

    fetchSlideData();
  }, [slideId, lessonId, navigate]);

  // 2. 导航逻辑
  const navRef = useRef<SlidePrevNextOut | null>(null);
  useEffect(() => { navRef.current = nav; }, [nav]);

  const goPrev = () => {
    if (navRef.current?.prev) navigate(`/student/lesson/${lessonId}/slide/${navRef.current.prev}`);
  };
  const goNext = () => {
    if (navRef.current?.next) navigate(`/student/lesson/${lessonId}/slide/${navRef.current.next}`);
  };

  // 3. 监听 Iframe 信号
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, direction } = event.data || {};
      if (type === 'NEXUS_NAVIGATE') {
        if (direction === 'next') goNext();
        if (direction === 'prev') goPrev();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [lessonId]);

  // 4. 全屏与键盘监听
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch(err => console.error(err));
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };

    document.addEventListener('fullscreenchange', handleFsChange);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 5. 拖拽逻辑
  const startDrag = (clientX: number, clientY: number, target: HTMLElement) => {
    isDraggingRef.current = true;
    const rect = target.getBoundingClientRect();

    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };

    if (!positionRef.current) {
      const initialPos = { x: rect.left, y: rect.top };
      setPosition(initialPos);
      positionRef.current = initialPos;
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    startDrag(e.clientX, e.clientY, e.currentTarget as HTMLElement);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation(); 
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY, e.currentTarget as HTMLElement);
  };

  useEffect(() => {
    const handleMove = (clientX: number, clientY: number) => {
      if (!isDraggingRef.current) return;

      const newPos = {
        x: clientX - dragOffsetRef.current.x,
        y: clientY - dragOffsetRef.current.y
      };

      const maxX = window.innerWidth - 50;
      const maxY = window.innerHeight - 50;
      const safePos = {
        x: Math.min(Math.max(0, newPos.x), maxX),
        y: Math.min(Math.max(0, newPos.y), maxY)
      };

      setPosition(safePos);
      positionRef.current = safePos;
    };

    const onMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault(); 
        const touch = e.touches[0];
        handleMove(touch.clientX, touch.clientY);
      }
    };

    const onEnd = () => {
      isDraggingRef.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  // 6. Iframe 内容构建
  const constructIframeContent = (htmlCode: string) => {
    const userInfo = JSON.stringify(user || null);
    const bridgeScript = `
      <script>
        // 注入父页面的用户信息
        window.parentUser = ${userInfo};
        window.parentToken = ${token ? JSON.stringify(token) : 'null'};
        
        // 导航函数
        window.requestNavigation = function(direction) {
          try { window.parent.postMessage({ type: 'NEXUS_NAVIGATE', direction: direction }, '*'); } 
          catch (e) { console.error(e); }
        };
        window.nextPage = function() { window.requestNavigation('next'); };
        window.prevPage = function() { window.requestNavigation('prev'); };
        
        // 获取用户信息的辅助函数
        window.getUserInfo = function() {
          return window.parentUser;
        };
        
        // 检查是否登录的辅助函数
        window.isLoggedIn = function() {
          return window.parentUser !== null;
        };
      </script>
    `;
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>body, html { margin: 0; padding: 0; width: 100%; height: 100%; overflow: hidden; background-color: white; }</style>
          ${bridgeScript}
        </head>
        <body>
          ${htmlCode}
        </body>
      </html>
    `;
  };

  useEffect(() => {
    if (slide) {
      console.log('🚀 Slide Loaded:', slide.slide_index, '/', totalSlides);
    }
  }, [slide, totalSlides]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-black text-white flex flex-col">

      {/* 核心播放区域 */}
      <div ref={containerRef} className="flex-1 relative bg-black flex items-center justify-center overflow-hidden group">

        {/* Loading */}
        {loading && (
          <div className="absolute inset-0 z-50 flex flex-col gap-3 items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity">
            <Loader2 className="animate-spin text-blue-500" size={48} />
            <p className="text-sm text-gray-400 animate-pulse">加载课件资源...</p>
          </div>
        )}

        {/* 返回按钮 */}
        {!isFullscreen && (
          <button
            onClick={() => navigate(`/student/lesson/${lessonId}`)}
            className="absolute top-4 left-4 z-40 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white/80 hover:text-white backdrop-blur-md transition-all border border-white/10 shadow-lg group/btn"
            title="返回课时详情"
          >
            <ArrowLeft size={20} className="group-hover/btn:-translate-x-0.5 transition-transform" />
          </button>
        )}

        {/* Iframe */}
        {slide && (
          <div className={`relative w-full max-w-[177.78vh] aspect-video shadow-2xl bg-white transition-opacity duration-500 ${loading ? 'opacity-0' : 'opacity-100'}`}>
            <iframe
              title={`Slide ${slide.id}`}
              srcDoc={constructIframeContent(slide.html_code)}
              className="w-full h-full border-0"
              allow="microphone *; camera *; midi *; encrypted-media *; autoplay *"
              sandbox="allow-scripts allow-forms allow-popups allow-modals allow-presentation allow-same-origin"
            />
          </div>
        )}

        {/* ✅ 教案抽屉 (美化版) */}
        <div 
          className={`absolute top-0 right-0 h-full w-[400px] bg-white text-gray-900 shadow-2xl z-[60] transform transition-transform duration-300 ease-in-out flex flex-col ${isGuideOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {/* 抽屉头部 */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-white shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                <BookOpen size={18} />
              </div>
              <h3 className="font-bold text-lg text-gray-800">教学指导</h3>
            </div>
            <button 
              onClick={() => setIsGuideOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* 抽屉内容区 (使用自动美化渲染器) */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50 custom-scrollbar">
            {slide?.side_guide ? (
              // ✅ 这里调用拆分后的组件
              <BeautifiedGuideRenderer htmlContent={slide.side_guide} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 opacity-60">
                <Sparkles size={48} className="mb-4 text-gray-300" />
                <p>暂无教案内容</p>
              </div>
            )}
          </div>
        </div>

        {/* 底部控制栏 */}
        {!loading && (
          <div
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            style={position ? {
              position: 'fixed',
              left: position.x,
              top: position.y,
              transform: 'none'
            } : {
              position: 'absolute',
              bottom: '1.5rem',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
            className={`
                flex items-center gap-3 px-4 py-2 
                bg-slate-900/60 backdrop-blur-md 
                rounded-full border border-white/10 shadow-xl 
                cursor-move select-none z-50
                touch-none
                hover:bg-slate-900/80 transition-colors
                ${isFullscreen && !position ? 'opacity-0 hover:opacity-100' : 'opacity-100'}
            `}
          >
            <GripHorizontal size={14} className="text-white/20 mr-1" />

            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                setIsGuideOpen(!isGuideOpen); 
              }} 
              className={`p-1.5 rounded-full transition-colors ${isGuideOpen ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'hover:bg-white/20 text-white'}`}
              title="查看教案"
            >
              <BookOpen size={18} />
            </button>

            <div className="w-px h-4 bg-white/20 mx-1" />

            <button onClick={(e) => { e.stopPropagation(); goPrev(); }} disabled={!nav?.prev} className="p-1.5 rounded-full hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition text-white"><ChevronLeft size={18} /></button>

            <span className="font-mono text-xs font-bold text-blue-300 min-w-[60px] text-center">
              {slide?.slide_index !== undefined ? slide.slide_index : '-'}
              <span className="text-gray-400 font-normal"> / {totalSlides || '-'}</span>
            </span>

            <button onClick={(e) => { e.stopPropagation(); goNext(); }} disabled={!nav?.next} className="p-1.5 rounded-full hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition text-white"><ChevronRight size={18} /></button>

            <div className="w-px h-4 bg-white/20 mx-1" />

            <button onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} className="p-1.5 rounded-full hover:bg-white/20 text-gray-300 transition">{isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>
            
            <div className="w-px h-4 bg-white/20 mx-1" />
            
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                window.open('/student/vibe-coding', '_blank', 'noopener,noreferrer'); 
              }}
              className="p-1.5 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white transition-all duration-300"
              title="进入 VibeCoding"
            >
              <span className="text-sm">🤖</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSlidePlayer;
